import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Palette, Download, Loader2, RefreshCw, Sparkles, Trash2 } from "lucide-react";
import api from "../lib/api";
import { isNoldDomain } from "../lib/domain";
import { useZone } from "../context/ZoneContext";

const KINDS = [
  { key: "tshirt-front", label: "T-shirt · Logo poitrine", desc: "Petit logo carré haute résolution, fond transparent. Pour impression avant." },
  { key: "tshirt-back",  label: "T-shirt · Grand logo dos", desc: "Composition large 28×35cm, motif marque + slogan. Pour impression au dos." },
  { key: "cap",          label: "Casquette · Patch centré",  desc: "Emblème rond 6cm, adapté à la broderie casquette (2-3 couleurs)." },
  { key: "banner-fb",    label: "Bannière Facebook",         desc: "16:9 photoréaliste chauffeur + slogan zone. 1200×628." },
  { key: "banner-whatsapp", label: "Story WhatsApp / Insta", desc: "1080×1080 carré, réservation d'un QR code coin bas-droite." },
  { key: "shop-sign",       label: "Panneau boutique/bureau", desc: "Enseigne 500×300mm avec coordonnées (téléphone, email, adresse), prêt à imprimer." },
];

export const BrandAssetsGenerator = () => {
  const { zone } = useZone();
  const isNold = zone === "europe" || isNoldDomain();
  const brand = isNold ? "nold" : "bentskin";
  const brandLabel = isNold ? "Nold Go" : "BentSkin Go";
  const [assets, setAssets] = useState([]);
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    try { const r = await api.get("/admin/brand-assets"); setAssets(r.data.assets || []); }
    catch { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async (kind) => {
    setBusy((b) => ({ ...b, [kind]: true }));
    toast.info("Génération en cours… (30-60s)");
    try {
      const r = await api.post("/admin/brand-assets/generate", { kind, brand });
      setAssets((prev) => [r.data, ...prev]);
      toast.success(`${KINDS.find((k) => k.key === kind)?.label || kind} · généré !`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Génération échouée");
    } finally {
      setBusy((b) => ({ ...b, [kind]: false }));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cet actif ?")) return;
    try { await api.delete(`/admin/brand-assets/${id}`); setAssets((prev) => prev.filter((a) => a.id !== id)); toast.success("Supprimé"); }
    catch { toast.error("Suppression impossible"); }
  };

  // Filtrer par marque active
  const myAssets = assets.filter((a) => a.brand === brand);

  return (
    <section className="bg-white rounded-3xl border border-[#E2DFD8] p-6" data-testid="brand-assets-generator">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F26419] to-[#D94F2B] flex items-center justify-center">
          <Palette className="w-5 h-5 text-white" />
        </div>
        <div>
          <div className="font-head font-black text-lg text-[#1A1A1A]">Actifs Marketing IA</div>
          <div className="text-xs text-[#75807A]">T-shirts · Casquettes · Bannières · marque {brandLabel}</div>
        </div>
      </div>

      {/* Générateurs par type */}
      <div className="grid gap-3">
        {KINDS.map((k) => (
          <div key={k.key} className="border border-[#E2DFD8] rounded-2xl p-4 hover:border-[#F26419] transition-colors" data-testid={`asset-card-${k.key}`}>
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-bold text-[#1A1A1A] text-sm">{k.label}</div>
                <div className="text-xs text-[#75807A] mt-0.5 leading-snug">{k.desc}</div>
              </div>
              <button data-testid={`asset-generate-${k.key}`} onClick={() => generate(k.key)} disabled={!!busy[k.key]}
                className="h-9 px-3 rounded-lg bg-[#F26419] text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-50 hover:bg-[#D94F2B]">
                {busy[k.key] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Générer
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Galerie des actifs déjà générés */}
      <div className="mt-6 border-t border-[#E2DFD8] pt-6">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs font-semibold text-[#75807A] uppercase tracking-wide">Galerie {brandLabel} ({myAssets.length})</div>
          <button onClick={load} className="text-xs text-[#3B423E] hover:text-[#F26419] flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Actualiser</button>
        </div>
        {myAssets.length === 0 ? (
          <div className="text-center text-[#75807A] text-xs py-8">Aucun actif encore généré. Cliquez sur "Générer" ci-dessus.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {myAssets.map((a) => {
              const kindLabel = KINDS.find((k) => k.key === a.kind)?.label || a.kind;
              return (
                <div key={a.id} className="bg-[#F4F0EA] rounded-xl overflow-hidden group border border-[#E2DFD8]" data-testid={`asset-item-${a.id}`}>
                  <div className="aspect-square bg-white flex items-center justify-center overflow-hidden">
                    <img src={a.url} alt={kindLabel} className="w-full h-full object-contain" />
                  </div>
                  <div className="p-2">
                    <div className="text-[11px] font-bold text-[#1A1A1A] truncate">{kindLabel}</div>
                    <div className="flex items-center gap-1 mt-1.5">
                      <a data-testid={`asset-download-${a.id}`} href={a.download_url} download className="flex-1 h-8 px-2 rounded-lg bg-[#1A1A1A] text-white text-[11px] font-bold flex items-center justify-center gap-1 hover:bg-black"><Download className="w-3 h-3" /> PNG</a>
                      <button data-testid={`asset-delete-${a.id}`} onClick={() => del(a.id)} className="w-8 h-8 rounded-lg bg-white border border-[#E2DFD8] flex items-center justify-center hover:border-red-500 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-4 text-[11px] text-[#75807A] leading-snug">
        💡 <b>Astuce imprimeur</b> — les PNG haute résolution générés ont un fond transparent (idéal t-shirts/casquettes).
        Pour des fichiers vectoriels SVG, ouvrez le PNG dans <a href="https://vectorizer.ai/" target="_blank" rel="noreferrer" className="underline">vectorizer.ai</a> (gratuit) pour convertir.
      </div>
    </section>
  );
};
